
import React, { useEffect } from "react";
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, Calendar, Clock, FileText, User, MapPin, CheckCircle, XCircle, AlertCircle, X } from "lucide-react";

type LeaveRequest = {
  id: string;
  user_id: string;
  leave_type: string;
  status: string;
  reason: string;
  start_date: string;
  end_date: string;
  created_at: string;
};

const LeaveRequestsList = () => {
  const { user } = useSupabaseUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: leaveRequests, isLoading } = useQuery({
    queryKey: ["leave-requests", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("leave_requests")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        toast({
          title: "Error loading leave requests",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }

      return data as LeaveRequest[];
    },
    enabled: !!user,
  });

  // Real-time subscription for leave requests
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('leave-requests-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leave_requests',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["leave-requests", user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("leave_requests")
        .update({ status: "cancelled" })
        .eq("id", id)
        .eq("user_id", user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leave-requests", user?.id] });
      toast({
        title: "Success",
        description: "Leave request cancelled successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel leave request",
        variant: "destructive",
      });
    },
  });

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "pending":
        return {
          color: "bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border-amber-200 dark:from-amber-900/20 dark:to-yellow-900/20 dark:text-amber-300 dark:border-amber-700",
          icon: <AlertCircle className="h-3 w-3" />,
          gradient: "from-amber-500 to-yellow-600"
        };
      case "approved":
        return {
          color: "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border-emerald-200 dark:from-emerald-900/20 dark:to-green-900/20 dark:text-emerald-300 dark:border-emerald-700",
          icon: <CheckCircle className="h-3 w-3" />,
          gradient: "from-emerald-500 to-green-600"
        };
      case "rejected":
        return {
          color: "bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border-red-200 dark:from-red-900/20 dark:to-rose-900/20 dark:text-red-300 dark:border-red-700",
          icon: <XCircle className="h-3 w-3" />,
          gradient: "from-red-500 to-rose-600"
        };
      case "cancelled":
        return {
          color: "bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border-gray-200 dark:from-gray-700/50 dark:to-slate-700/50 dark:text-gray-300 dark:border-gray-600",
          icon: <X className="h-3 w-3" />,
          gradient: "from-gray-500 to-slate-600"
        };
      default:
        return {
          color: "bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border-blue-200 dark:from-blue-900/20 dark:to-indigo-900/20 dark:text-blue-300 dark:border-blue-700",
          icon: <FileText className="h-3 w-3" />,
          gradient: "from-blue-500 to-indigo-600"
        };
    }
  };

  const getLeaveTypeConfig = (type: string) => {
    switch (type) {
      case "vacation":
        return {
          label: "Vacation/Holiday",
          icon: <MapPin className="h-4 w-4" />,
          gradient: "from-purple-500 to-pink-600"
        };
      case "sick":
        return {
          label: "Sick Leave",
          icon: <User className="h-4 w-4" />,
          gradient: "from-red-500 to-orange-600"
        };
      case "personal":
        return {
          label: "Personal Leave",
          icon: <User className="h-4 w-4" />,
          gradient: "from-blue-500 to-cyan-600"
        };
      default:
        return {
          label: type.charAt(0).toUpperCase() + type.slice(1),
          icon: <FileText className="h-4 w-4" />,
          gradient: "from-gray-500 to-gray-600"
        };
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <div className="flex items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-lg text-muted-foreground">Loading your requests...</span>
        </div>
      </div>
    );
  }

  if (!leaveRequests || leaveRequests.length === 0) {
    return (
      <div className="text-center p-12">
        <div className="flex flex-col items-center gap-4">
          <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full">
            <Calendar className="h-12 w-12 text-gray-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">No leave requests found</h3>
            <p className="text-muted-foreground mt-1">You haven't submitted any leave requests yet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {leaveRequests.map((request) => {
        const statusConfig = getStatusConfig(request.status);
        const typeConfig = getLeaveTypeConfig(request.leave_type);
        
        return (
          <Card key={request.id} className="group hover:shadow-xl transition-all duration-300 hover:scale-105 border-0 shadow-lg overflow-hidden bg-white dark:bg-gray-800">
            {/* Gradient Header */}
            <div className={`h-2 bg-gradient-to-r ${statusConfig.gradient}`}></div>
            
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className={`p-2 bg-gradient-to-r ${typeConfig.gradient} rounded-lg text-white`}>
                    {typeConfig.icon}
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {typeConfig.label}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Request #{request.id.slice(-8)}
                    </p>
                  </div>
                </div>
                <Badge className={`${statusConfig.color} font-medium px-3 py-1 shadow-sm`}>
                  <div className="flex items-center gap-1">
                    {statusConfig.icon}
                    {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                  </div>
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="pb-4 space-y-4">
              {/* Date Range */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>From:</span>
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {format(fromLocalISOString(request.start_date), "MMM d, yyyy")}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>To:</span>
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {format(fromLocalISOString(request.end_date), "MMM d, yyyy")}
                  </span>
                </div>
              </div>

              {/* Reason */}
              {request.reason && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Reason:</span>
                  </div>
                  <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed">
                    {request.reason}
                  </p>
                </div>
              )}
            </CardContent>
            
            <CardFooter className="pt-4 border-t bg-gray-50/50 dark:bg-gray-800/50 flex justify-between items-center">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>Requested {format(new Date(request.created_at), "MMM d, yyyy")}</span>
              </div>
              
              {request.status === "pending" && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => cancelMutation.mutate(request.id)}
                  disabled={cancelMutation.isPending}
                  className="shadow-sm hover:shadow-md transition-all duration-200"
                >
                  {cancelMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </>
                  )}
                </Button>
              )}
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
};

export default LeaveRequestsList;
